import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Divider,
  Snackbar,
  Alert,
  MenuItem,
  IconButton,
  Collapse,
} from "@mui/material";
import { ExpandLess, ExpandMore, ControlPoint, SortByAlpha } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import { FaFolderOpen } from 'react-icons/fa';
import { BlobServiceClient, BlobClient } from '@azure/storage-blob';
import { nanoid } from 'nanoid';
import { LoadToCosmos, SendToCosmos } from './SendToCosmos';
import { ModeContext } from './ModeContext';

// ナレッジ/FAQ/ルール登録コンポーネント
const SettingsKnowledgeFaq = () => {
  const { loginInfo } = useContext(ModeContext);
  const [loading, setLoading] = useState(true);
  const [itemList, setItemList] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isNewMode, setIsNewMode] = useState(false);
  const [openTypes, setOpenTypes] = useState({});
  const [openCategories, setOpenCategories] = useState({});
  const [openSubCategories, setOpenSubCategories] = useState({});
  const [sortOrderType, setSortOrderType] = useState({});
  const [sortOrderCat, setSortOrderCat] = useState({});
  const [sortOrderSubCat, setSortOrderSubCat] = useState({});
  const [draft, setDraft] = useState({
    type: "",
    title: "",
    content: "",
    answer: "",
    category: "",
    sub_category: "",
    tag: "",
    status: "",
  });
  // 複数画像アップロード関連
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImageUrls, setExistingImageUrls] = useState([]);
  const [removedImageUrls, setRemovedImageUrls] = useState([]);
  const fileInputRef = useRef(null);

  // 既存データからimages配列を復元するヘルパー（後方互換対応）
  const parseImages = (item) => {
    // URLからSASトークンを除去するヘルパー
    const cleanUrl = (url) => url ? url.split('?')[0] : '';
    const cleanUrls = (urls) => urls.filter(Boolean).map(cleanUrl).filter(Boolean);

    // 新形式: images配列フィールド
    if (item?.images && Array.isArray(item.images)) {
      return cleanUrls(item.images);
    }
    // 旧形式: imageUrl（JSON配列文字列 or 単一URL）
    const value = item?.imageUrl;
    if (!value) return [];
    if (Array.isArray(value)) return cleanUrls(value);
    if (typeof value === 'string' && value.startsWith('[')) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return cleanUrls(parsed);
      } catch { /* パース失敗時は単一URLとして扱う */ }
    }
    return value ? [cleanUrl(value)] : [];
  };

  useEffect(() => {
    fetchList();
  }, []);

  useEffect(() => {
    if (selectedItem) {
      setDraft({
        type: selectedItem.type || "",
        title: selectedItem.title || "",
        content: selectedItem.content || "",
        answer: selectedItem.answer || "",
        category: selectedItem.category || "",
        sub_category: selectedItem.sub_category || "",
        tag: selectedItem.tag || "",
        status: selectedItem.status || "",
      });
      // 既存の画像URLを配列として復元
      setExistingImageUrls(parseImages(selectedItem));
      setImageFiles([]);
      setImagePreviews([]);
      setRemovedImageUrls([]);
    }
  }, [selectedItem]);

  // データ取得（db-teraoka-gpt / db_conversation_history / conversations02 / type=007）
  const fetchList = async () => {
    try {
      const payload = {
        id: "all",
        container: "conversations02",
        type1: "sp-faq",
        type2: "sp-rule",

      };
      const items = await LoadToCosmos(payload);
      const list = items.item
        .filter((item) => item.status !== 'delete')
        .map((item) => ({
        id: item.id,
        user: item.user || "",
        type: item.type || "",
        category: item.category || "",
        sub_category: item.sub_category || "",
        group: item.group || "",
        range: item.range || "",
        display_name: item.display_name || "",
        title: item.title || "",
        content: item.content || "",
        text: item.text || "",
        answer: item.answer || "",
        site_name: item.site_name || "",
        URL: item.URL || "",
        status: item.status || "",
        tag: item.tag || "",
        update_at: item.update_at || "",
        images: item.images || [],
        imageUrl: item.imageUrl || "",
      }));
      setItemList(list);
    } catch (error) {
      console.error("データ取得エラー:", error);
    } finally {
      setLoading(false);
    }
  };

  // タイプ名のラベル変換
  const typeLabel = (type) => {
    if (type === 'sp-faq') return 'FAQ';
    if (type === 'sp-rule') return 'ルール/注意事項';
    return type || '未分類';
  };

  // データをタイプ→カテゴリー→サブカテゴリにグルーピング
  const getGroupedData = () => {
    const grouped = {};
    itemList.forEach((item) => {
      const typeName = item.type || '未分類';
      const catName = item.category || '未分類';
      const subCatName = item.sub_category || '未分類';
      if (!grouped[typeName]) grouped[typeName] = {};
      if (!grouped[typeName][catName]) grouped[typeName][catName] = {};
      if (!grouped[typeName][catName][subCatName]) grouped[typeName][catName][subCatName] = [];
      grouped[typeName][catName][subCatName].push(item);
    });
    return grouped;
  };

  const toggleType = (key) => {
    setOpenTypes((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const toggleCategory = (key) => {
    setOpenCategories((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const toggleSubCategory = (key) => {
    setOpenSubCategories((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ソートトグル
  const handleSortType = (typeKey, e) => {
    e.stopPropagation();
    setSortOrderType((prev) => ({ ...prev, [typeKey]: prev[typeKey] === 'asc' ? 'desc' : 'asc' }));
  };
  const handleSortCat = (catKey, e) => {
    e.stopPropagation();
    setSortOrderCat((prev) => ({ ...prev, [catKey]: prev[catKey] === 'asc' ? 'desc' : 'asc' }));
  };
  const handleSortSubCat = (subCatKey, e) => {
    e.stopPropagation();
    setSortOrderSubCat((prev) => ({ ...prev, [subCatKey]: prev[subCatKey] === 'asc' ? 'desc' : 'asc' }));
  };

  // ソート済みエントリを返す
  const sortEntries = (entries, order) => {
    if (!order) return entries;
    return [...entries].sort(([a], [b]) =>
      order === 'asc' ? a.localeCompare(b, 'ja') : b.localeCompare(a, 'ja')
    );
  };
  const sortItems = (items, order) => {
    if (!order) return items;
    return [...items].sort((a, b) =>
      order === 'asc' ? (a.title || '').localeCompare(b.title || '', 'ja') : (b.title || '').localeCompare(a.title || '', 'ja')
    );
  };

  // ユニークID生成（sp-XXXXXXX）
  const generateId = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = 'sp-';
    for (let i = 0; i < 7; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // 現在日時をフォーマット
  const formatNow = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  // 画像ファイル選択ハンドラー（複数対応）
  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const validFiles = [];
    const validPreviews = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        alert(`「${file.name}」は画像ファイルではありません。スキップします。`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert(`「${file.name}」は5MBを超えています。スキップします。`);
        continue;
      }
      validFiles.push(file);
      validPreviews.push(URL.createObjectURL(file));
    }
    if (validFiles.length > 0) {
      setImageFiles((prev) => [...prev, ...validFiles]);
      setImagePreviews((prev) => [...prev, ...validPreviews]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // 新規追加画像の個別削除
  const handleRemoveNewImage = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed);
      return prev.filter((_, i) => i !== index);
    });
  };

  // 既存画像の個別削除（保存時にBlobも削除するため追跡）
  const handleRemoveExistingImage = (index) => {
    const removedUrl = existingImageUrls[index];
    if (removedUrl) {
      setRemovedImageUrls((prev) => [...prev, removedUrl]);
    }
    setExistingImageUrls((prev) => prev.filter((_, i) => i !== index));
  };

  // Blob Storageから画像を削除
  const deleteImageFromBlob = async (imageUrl) => {
    try {
      const cleanUrl = imageUrl.split('?')[0];
      const blob3Base = process.env.REACT_APP_BLOB3_URL;
      if (!cleanUrl.includes(new URL(blob3Base).hostname)) return;
      const blobPath = new URL(cleanUrl).pathname.split('/').slice(2).join('/');
      const deleteSasUrl = `${blob3Base}/${blobPath}?${process.env.REACT_APP_BLOB3_TOKEN}`;
      const blobClient = new BlobClient(deleteSasUrl);
      await blobClient.delete();
    } catch (err) {
      console.error('Blob画像削除エラー:', err);
    }
  };

  // 画像をBlob Storageにアップロード（SASトークンなしのURLを返す）
  const uploadImageToBlob = async (file, docId) => {
    const containerSasUrl = `${process.env.REACT_APP_BLOB3_URL}?${process.env.REACT_APP_BLOB3_TOKEN}`;
    const containerClient = new BlobServiceClient(containerSasUrl).getContainerClient('');
    const uniqueId = nanoid();
    const ext = file.name.split('.').pop();
    const blobName = `knowledge/${docId}/${uniqueId}.${ext}`;
    const blockBlobClient = containerClient.getBlockBlobClient(blobName);
    await blockBlobClient.uploadBrowserData(file, {
      blobHTTPHeaders: { blobContentType: file.type },
    });
    return blockBlobClient.url.split('?')[0];
  };

  // 画像URLにSASトークンを付与して表示用URLを生成
  const getImageDisplayUrl = (url) => {
    if (!url) return '';
    if (url.includes('?')) return url;
    return `${url}?${process.env.REACT_APP_BLOB3_TOKEN}`;
  };

  // 階層プリセット付き新規追加
  const handleNewWithPreset = (type, category, sub_category, e) => {
    e.stopPropagation();
    const newItem = {
      id: generateId(),
      user: loginInfo?.userId || "",
      type: type || "sp-faq",
      category: category || "",
      sub_category: sub_category || "",
      group: "ServicePortal",
      range: "All",
      display_name: loginInfo?.userName || "",
      title: "",
      content: "",
      text: "",
      answer: "",
      site_name: "",
      URL: "",
      status: "publish",
      tag: "",
      update_at: formatNow(),
    };
    setSelectedItem(newItem);
    setIsNewMode(true);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedItem(null);
    setIsNewMode(false);
    // 画像stateリセット
    imagePreviews.forEach((p) => URL.revokeObjectURL(p));
    setImageFiles([]);
    setImagePreviews([]);
    setExistingImageUrls([]);
    setRemovedImageUrls([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // 新規追加（ヘッダーボタン）
  const handleNewItem = () => {
    const newItem = {
      id: generateId(),
      user: loginInfo?.userId || "",
      type: "sp-faq",
      category: "",
      sub_category: "",
      group: "ServicePortal",
      range: "All",
      display_name: loginInfo?.userName || "",
      title: "",
      content: "",
      text: "",
      answer: "",
      site_name: "",
      URL: "",
      status: "publish",
      tag: "",
      update_at: formatNow(),
    };
    setSelectedItem(newItem);
    setIsNewMode(true);
    setOpenModal(true);
  };

  // 保存処理
  const handleSave = async () => {
    if (!selectedItem) return;

    // 新規画像をすべてアップロード
    const newUploadedUrls = [];
    for (const file of imageFiles) {
      const url = await uploadImageToBlob(file, selectedItem.id);
      newUploadedUrls.push(url);
    }

    // 既存画像URL + 新規アップロードURLを結合
    const allImageUrls = [...existingImageUrls, ...newUploadedUrls];

    // 画像URLからimgタグを生成してtextフィールドに格納（表示用にSASトークン付き）
    const imageText = allImageUrls
      .map((url) => `<img src="${getImageDisplayUrl(url)}" alt="${draft.title || 'ナレッジ画像'}">`)
      .join('\n');

    const payload = {
      ...selectedItem,
      type: draft.type,
      title: draft.title,
      content: draft.content,
      answer: draft.answer,
      category: draft.category,
      sub_category: draft.sub_category,
      tag: draft.tag,
      status: draft.status,
      text: imageText,
      images: allImageUrls,
      container: "conversations02",
    };
    try {
      await SendToCosmos(payload);

      // 削除された既存画像のBlobを削除
      for (const url of removedImageUrls) {
        await deleteImageFromBlob(url);
      }

      handleCloseModal();
      fetchList();
    } catch (error) {
      console.error("保存エラー:", error);
    }
  };

  // 論理削除処理（statusを"delete"に更新）
  const handleDelete = async () => {
    if (!selectedItem || isNewMode) return;
    const ok = window.confirm('この項目を削除しますか？');
    if (!ok) return;
    try {
      // 記事に紐づく全画像のBlobを削除
      const allImages = [...existingImageUrls, ...removedImageUrls];
      for (const url of allImages) {
        await deleteImageFromBlob(url);
      }

      const payload = {
        ...selectedItem,
        status: 'delete',
        update_at: formatNow(),
        container: 'conversations02',
      };
      await SendToCosmos(payload);
      handleCloseModal();
      fetchList();
      setSnackbarOpen(true);
    } catch (error) {
      console.error('削除エラー:', error);
    }
  };

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6" sx={{ fontSize: 16 }}>
          ナレッジ/FAQ/ルール一覧
        </Typography>
        <Button variant="contained" size="small" onClick={handleNewItem}>
          新規追加
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <Box sx={{ maxHeight: 'calc(100vh - 180px)', overflow: 'auto' }}>
          <List dense>
            <Divider />
            {Object.entries(getGroupedData()).map(([typeName, categories]) => (
              <React.Fragment key={typeName}>
                {/* 第1階層：タイプ */}
                <ListItem button onClick={() => toggleType(typeName)} sx={{ py: 0.5, bgcolor: '#f5f5f5' }}>
                  <ListItemText primary={typeLabel(typeName)} primaryTypographyProps={{ fontWeight: 'bold' }} />
                  <IconButton edge="end" size="small" sx={{ mr: 0.5 }} onClick={(e) => handleSortType(typeName, e)}
                    title={sortOrderType[typeName] === 'asc' ? '降順にソート' : '昇順にソート'}>
                    <SortByAlpha sx={{ fontSize: 18 }} color={sortOrderType[typeName] ? 'primary' : 'disabled'} />
                  </IconButton>
                  <IconButton edge="end" size="small" sx={{ mr: 1 }} onClick={(e) => handleNewWithPreset(typeName, '', '', e)}>
                    <ControlPoint fontSize="small" />
                  </IconButton>
                  {openTypes[typeName] ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                <Divider />

                <Collapse in={openTypes[typeName]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding dense>
                    {sortEntries(Object.entries(categories), sortOrderType[typeName]).map(([catName, subCategories]) => {
                      const catKey = `${typeName}-${catName}`;
                      return (
                        <React.Fragment key={catKey}>
                          {/* 第2階層：カテゴリー */}
                          <ListItem button sx={{ pl: 4, py: 0.3 }} onClick={() => toggleCategory(catKey)}>
                            <FaFolderOpen style={{ marginRight: 8, color: '#FFCC00', fontSize: '20px' }} />
                            <ListItemText primary={catName} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                            <IconButton edge="end" size="small" sx={{ mr: 0.5 }} onClick={(e) => handleSortCat(catKey, e)}
                              title={sortOrderCat[catKey] === 'asc' ? '降順にソート' : '昇順にソート'}>
                              <SortByAlpha sx={{ fontSize: 16 }} color={sortOrderCat[catKey] ? 'primary' : 'disabled'} />
                            </IconButton>
                            <IconButton edge="end" size="small" sx={{ mr: 0.5 }} onClick={(e) => handleNewWithPreset(typeName, catName, '', e)}>
                              <ControlPoint sx={{ fontSize: 18 }} />
                            </IconButton>
                            {openCategories[catKey] ? <ExpandLess /> : <ExpandMore />}
                          </ListItem>

                          <Collapse in={openCategories[catKey]} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding dense>
                              {sortEntries(Object.entries(subCategories), sortOrderCat[catKey]).map(([subCatName, items]) => {
                                const subCatKey = `${catKey}-${subCatName}`;
                                return (
                                  <React.Fragment key={subCatKey}>
                                    {/* 第3階層：サブカテゴリ */}
                                    <ListItem button sx={{ pl: 6, py: 0.2 }} onClick={() => toggleSubCategory(subCatKey)}>
                                      <FaFolderOpen style={{ marginRight: 8, color: '#90CAF9', fontSize: '18px' }} />
                                      <ListItemText primary={subCatName} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                                      <IconButton edge="end" size="small" sx={{ mr: 0.5 }} onClick={(e) => handleSortSubCat(subCatKey, e)}
                                        title={sortOrderSubCat[subCatKey] === 'asc' ? '降順にソート' : '昇順にソート'}>
                                        <SortByAlpha sx={{ fontSize: 16 }} color={sortOrderSubCat[subCatKey] ? 'primary' : 'disabled'} />
                                      </IconButton>
                                      <IconButton edge="end" size="small" sx={{ mr: 0.5 }} onClick={(e) => handleNewWithPreset(typeName, catName, subCatName, e)}>
                                        <ControlPoint sx={{ fontSize: 16 }} />
                                      </IconButton>
                                      {openSubCategories[subCatKey] ? <ExpandLess /> : <ExpandMore />}
                                    </ListItem>

                                    <Collapse in={openSubCategories[subCatKey]} timeout="auto" unmountOnExit>
                                      <List component="div" disablePadding dense>
                                        {sortItems(items, sortOrderSubCat[subCatKey]).map((item) => (
                                          <ListItem
                                            key={item.id}
                                            button
                                            sx={{ pl: 8, py: 0, minHeight: '26px' }}
                                            onClick={() => {
                                              setSelectedItem(item);
                                              setOpenModal(true);
                                            }}
                                          >
                                            <ListItemText
                                              primary={
                                                <Typography sx={{ fontSize: '0.8rem' }}>
                                                  {item.title}
                                                </Typography>
                                              }
                                            />
                                          </ListItem>
                                        ))}
                                      </List>
                                    </Collapse>
                                  </React.Fragment>
                                );
                              })}
                            </List>
                          </Collapse>
                        </React.Fragment>
                      );
                    })}
                  </List>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        </Box>
      )}

      {/* 編集モーダル */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="false" fullWidth>
        <DialogTitle sx={{ fontSize: 16 }}>{isNewMode ? 'ナレッジ/FAQ/ルール新規追加' : 'ナレッジ/FAQ/ルール編集'}</DialogTitle>
        <DialogContent dividers>
          {selectedItem && (
            <>
              <TextField
                fullWidth margin="dense" label="タイプ" size="small" select
                value={draft.type}
                onChange={(e) => setDraft((prev) => ({ ...prev, type: e.target.value }))}
              >
                <MenuItem value="sp-faq">FAQ</MenuItem>
                <MenuItem value="sp-rule">ルール/注意事項</MenuItem>
              </TextField>
              <TextField
                fullWidth margin="dense" label="タイトル" size="small"
                value={draft.title}
                onChange={(e) => setDraft((prev) => ({ ...prev, title: e.target.value }))}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  margin="dense" label="カテゴリー" size="small" sx={{ flex: 1 }}
                  placeholder="カテゴリー(フォルダ)"
                  value={draft.category}
                  onChange={(e) => setDraft((prev) => ({ ...prev, category: e.target.value }))}
                />
                <TextField
                  margin="dense" label="機種" size="small" sx={{ flex: 1 }}
                  placeholder="特になければ空白"
                  value={draft.sub_category}
                  onChange={(e) => setDraft((prev) => ({ ...prev, sub_category: e.target.value }))}
                />
              </Box>

              <TextField
                fullWidth margin="dense" label="お客様" multiline rows={1} size="small"
                placeholder="指定のお客様がいなければ空白"
                value={draft.content}
                onChange={(e) => setDraft((prev) => ({ ...prev, content: e.target.value }))}
              />
              <TextField
                fullWidth margin="dense" label="内容" multiline rows={6} size="small"
                placeholder="FAQやルール／注意事項を入力してください。"
                value={draft.answer}
                onChange={(e) => setDraft((prev) => ({ ...prev, answer: e.target.value }))}
              />

              {/* 画像アップロードエリア（複数対応） */}
              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontSize: '0.85rem' }}>画像（任意・複数選択可）</Typography>
                {/* 既存画像のプレビュー */}
                {existingImageUrls.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    {existingImageUrls.map((url, index) => (
                      <Box key={`existing-${index}`} sx={{ position: 'relative', display: 'inline-block' }}>
                        <img
                          src={getImageDisplayUrl(url)}
                          alt={`既存画像${index + 1}`}
                          style={{
                            maxWidth: 150,
                            maxHeight: 120,
                            borderRadius: 4,
                            border: '1px solid #ccc',
                            objectFit: 'contain',
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveExistingImage(index)}
                          sx={{
                            position: 'absolute', top: -8, right: -8,
                            bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                          }}
                          aria-label="画像を削除"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
                {/* 新規追加画像のプレビュー */}
                {imagePreviews.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    {imagePreviews.map((preview, index) => (
                      <Box key={`new-${index}`} sx={{ position: 'relative', display: 'inline-block' }}>
                        <img
                          src={preview}
                          alt={`新規画像${index + 1}`}
                          style={{
                            maxWidth: 150,
                            maxHeight: 120,
                            borderRadius: 4,
                            border: '1px solid #4caf50',
                            objectFit: 'contain',
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveNewImage(index)}
                          sx={{
                            position: 'absolute', top: -8, right: -8,
                            bgcolor: 'rgba(0,0,0,0.6)', color: '#fff',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                          }}
                          aria-label="画像を削除"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
                {/* ファイル選択ボタン（常に表示して追加可能） */}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ImageIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ textTransform: 'none' }}
                >
                  画像を選択
                </Button>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  style={{ display: 'none' }}
                />
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                  ※ 対応形式: JPG, PNG, GIF（各5MB以下・複数選択可）
                </Typography>
              </Box>

              <TextField
                fullWidth margin="dense" label="グループ" size="small"
                placeholder="グループ"
                value={draft.tag}
                onChange={(e) => setDraft((prev) => ({ ...prev, tag: e.target.value }))}
              />
              <TextField
                fullWidth margin="dense" label="公開設定" size="small" select
                value={draft.status}
                onChange={(e) => setDraft((prev) => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="publish">公開</MenuItem>
                <MenuItem value="private">非公開</MenuItem>
              </TextField>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {!isNewMode && (
            <Button color="error" onClick={handleDelete} sx={{ mr: 'auto' }}>削除</Button>
          )}
          <Button onClick={handleCloseModal}>閉じる</Button>
          <Button variant="contained" color="primary" onClick={() => setConfirmOpen(true)}>
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 保存確認 */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle sx={{ fontSize: 16 }}>保存確認</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>保存しますか？</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>いいえ</Button>
          <Button
            variant="contained" color="primary"
            onClick={async () => {
              await handleSave();
              setConfirmOpen(false);
              setSnackbarOpen(true);
            }}
          >
            はい
          </Button>
        </DialogActions>
      </Dialog>

      {/* 保存完了メッセージ */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          保存しました。
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsKnowledgeFaq;
